import { toast } from 'react-hot-toast';
import { AxiosError } from 'axios';
import { Dayjs } from 'dayjs';
import {
  getGroupSchedule,
  getTeacherSchedule,
  resetGroupSchedule,
  resetTeacherSchedule,
  setGroupSchedule,
  setSchedulePublicIsGroupSchedule,
  setTeacherSchedule,
} from 'entities/index';
import { useInvisibleCaptcha } from 'features/index';
import {
  isNonEmptyStr,
  isPositiveIntStr,
  useAppDispatch,
  useAppSelector,
} from 'shared/lib';

export const useSchedule = () => {
  const {
    requestText,
    requestTeacherId,
    startIntervalDate,
    isGroupSchedule,
    endIntervalDate,
  } = useAppSelector((state) => state.schedulePublicReducer);

  const {
    groupSchedule,
    groupScheduleWithoutDate,
    groupScheduleIsLoading,
    dataForGroupSchedule,
  } = useAppSelector((state) => state.groupsReducer);
  const {
    teacherSchedule,
    teacherScheduleIsLoading,
    dataForTeacherSchedule,
    teacherScheduleWithoutDate,
  } = useAppSelector((state) => state.teachersReducer);

  const { token, captchaStatus } = useInvisibleCaptcha();

  const dispatch = useAppDispatch();

  const scheduleQuery = async () => {
    try {
      if (token) {
        if (requestText && isNonEmptyStr(requestText as string)) {
          const trimmed = requestText.trim();
          if (isPositiveIntStr(trimmed)) {
            dispatch(resetTeacherSchedule());
            dispatch(setSchedulePublicIsGroupSchedule(true));
            await dispatch(
              getGroupSchedule({
                group: requestText as string,
                startDate: startIntervalDate,
                endDate: endIntervalDate,
              }),
            ).unwrap();
          } else {
            dispatch(resetGroupSchedule());
            dispatch(setSchedulePublicIsGroupSchedule(false));
            await dispatch(
              getTeacherSchedule({
                teacherID: requestTeacherId as string,
                startDate: startIntervalDate,
                endDate: endIntervalDate,
              }),
            ).unwrap();
          }
        }
      }
    } catch (error) {
      let errorMessage = 'Ошибка при выполнении поиска';
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          console.error(error);
          errorMessage = '';
        } else {
          errorMessage = error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      if (errorMessage) toast.error(errorMessage);
    }
  };

  const filterSchedule = (startDate: Dayjs, endDate: Dayjs) => {
    if (isGroupSchedule) {
      dispatch(
        setGroupSchedule({
          startDate,
          endDate,
        }),
      );
    } else {
      dispatch(
        setTeacherSchedule({
          startDate,
          endDate,
        }),
      );
    }
  };

  const isReady = isGroupSchedule
    ? groupSchedule.length !== 0
    : teacherSchedule.length !== 0;

  const isSkeleton =
    teacherScheduleIsLoading ||
    groupScheduleIsLoading ||
    !token ||
    captchaStatus !== 'success' ||
    !isReady;

  return {
    scheduleQuery,
    filterSchedule,
    isReady,
    isSkeleton,
    groupSchedule,
    groupScheduleWithoutDate,
    teacherSchedule,
    token,
    requestText,
    isGroupSchedule,
    dataForGroupSchedule,
    dataForTeacherSchedule,
    teacherScheduleWithoutDate,
  };
};
